import { create } from 'zustand';
import { resetBreadcrumbs } from '@/layouts/AppLayout/breadcrumbs';
import type { User } from '@/api/types';

/**
 * Состояние входа:
 * - anonymous — сессии нет;
 * - active — сессия действует;
 * - reauth_required — сервер больше не принимает сессию (истёк refresh, абсолютный
 *   срок, отзыв). Пользователь и смонтированные страницы сохраняются в памяти, чтобы
 *   повторный вход того же пользователя вернул его к незавершённой форме (E1.4).
 */
export type AuthState = 'anonymous' | 'active' | 'reauth_required';

interface SessionState {
	/** Маршруты рабочего места смонтированы: active или reauth_required */
	isAuthorized: boolean;
	authState: AuthState;
	user: User | null;
	token: string | null;
	/** Поколение входа: новая сессия/выход меняют его, ротация токена — нет */
	generation: number;
	/** Путь, на котором был пользователь перед logout (для редиректа после входа) */
	prevPath: string | null;
	setSession: (user: User, token: string) => void;
	clearSession: () => void;
	setPrevPath: (path: string) => void;
	/** Восстановление getSession; false — запрос принадлежит уже завершённому поколению */
	restoreSession: (user: User, token: string, expectedGeneration?: number) => boolean;
	/** Сервер требует повторного входа; без сессии ничего не меняет */
	requireReauth: () => void;
	/** Убирает токен из хранилища и памяти; пользователь и состояние входа не меняются */
	clearToken: () => void;
	/** Сервер выдал новый токен той же сессии: состояние входа не меняется */
	updateToken: (token: string) => void;
}

/**
 * Идентичность сессии: компания и пользователь. Смена идентичности или выход
 * очищают кеш данных прежнего пользователя (bindCacheToSessionIdentity).
 */
export function sessionIdentity(user: User | null): string | null {
	if (!user) return null;
	return `${user.company_id ?? ''}:${user.id}`;
}

export const useSessionStore = create<SessionState>((set, get) => ({
	isAuthorized: false,
	authState: 'anonymous',
	user: null,
	token: null,
	generation: 0,
	prevPath: null,
	setSession: (user, token) => {
		if (sessionIdentity(get().user) !== sessionIdentity(user)) resetBreadcrumbs();
		localStorage.setItem('Token', token);
		set({ isAuthorized: true, authState: 'active', user, token, generation: get().generation + 1 });
	},
	clearSession: () => {
		resetBreadcrumbs();
		localStorage.removeItem('Token');
		set({
			isAuthorized: false,
			authState: 'anonymous',
			user: null,
			token: null,
			generation: get().generation + 1,
		});
	},
	setPrevPath: (path) => set({ prevPath: path }),
	restoreSession: (user, token, expectedGeneration = get().generation) => {
		if (get().generation !== expectedGeneration) return false;
		const changedIdentity = sessionIdentity(get().user) !== sessionIdentity(user);
		if (changedIdentity) resetBreadcrumbs();
		set({
			isAuthorized: true,
			authState: 'active',
			user,
			token,
			generation: get().generation + (changedIdentity ? 1 : 0),
		});
		return true;
	},
	requireReauth: () => {
		if (get().user) {
			set({ authState: 'reauth_required' });
		}
	},
	clearToken: () => {
		localStorage.removeItem('Token');
		set({ token: null });
	},
	updateToken: (token) => {
		if (!get().user) return;
		localStorage.setItem('Token', token);
		set({ token });
	},
}));
